import type { MaybeRefOrGetter } from 'vue';
import type { ResolvedConnectorDefinition } from '#layers/data-model/types/data-connectors/define';
import type { ResolvedMonospaceDataSource } from '#layers/data-model/types/schema/resolved-schema';
import { computed, toValue } from 'vue';
import { useDataSourceConnectors } from '#layers/data-model/composables/use-data-source-connectors';

type ConnectorLookupMap = Record<string, ResolvedConnectorDefinition<any>>;
type ConnectorLookupId = string | null | undefined;

type ConnectorSource = Pick<ResolvedMonospaceDataSource, 'provider' | 'preset'>;

/** Icon shown for a data source whose connector has no icon of its own. */
export const DEFAULT_CONNECTOR_ICON = 'lucide:database';

export function getDataSourceConnectorDefinition(
	ids: Iterable<ConnectorLookupId>,
	connectorsById: ConnectorLookupMap,
	connectorsByProvider: ConnectorLookupMap,
) {
	for (const id of ids) {
		if (!id) continue;

		const connector = connectorsByProvider[id] ?? connectorsById[id];
		if (connector) return connector;
	}
}

/**
 * Resolve the connector definition backing a data source, preferring its
 * preset (e.g. Supabase) over the bare provider (e.g. PostgreSQL).
 */
export function getConnectorForDataSource(
	source: ConnectorSource,
	connectorsById: ConnectorLookupMap,
	connectorsByProvider: ConnectorLookupMap,
) {
	const provider = String(source.provider);
	const preset = source.preset ? String(source.preset) : undefined;

	return getDataSourceConnectorDefinition([preset, provider], connectorsById, connectorsByProvider);
}

/** Props for `ConnectorLogo`, falling back to a generic database icon. */
export function getConnectorLogoProps(
	source: ConnectorSource,
	connectorsById: ConnectorLookupMap,
	connectorsByProvider: ConnectorLookupMap,
) {
	const connector = getConnectorForDataSource(source, connectorsById, connectorsByProvider);

	return {
		icon: connector?.icon ?? DEFAULT_CONNECTOR_ICON,
		iconColor: connector?.iconColor,
	};
}

/** Human-readable connector name, falling back to the raw provider. */
export function getConnectorName(
	source: ConnectorSource,
	connectorsById: ConnectorLookupMap,
	connectorsByProvider: ConnectorLookupMap,
) {
	return getConnectorForDataSource(source, connectorsById, connectorsByProvider)?.name ?? source.provider;
}

export function useDataSourceConnectorDefinition(
	...providerIds: MaybeRefOrGetter<ConnectorLookupId>[]
) {
	const { connectorsById, connectorsByProvider } = useDataSourceConnectors();

	return computed(() => {
		return getDataSourceConnectorDefinition(
			providerIds.map((providerId) => toValue(providerId)),
			connectorsById.value,
			connectorsByProvider.value,
		);
	});
}
